import { Button } from "@/components/ui/button";
import { MessageSquare, Video, AlertTriangle, Check, RefreshCw } from "lucide-react";
import { CaregiverSpecialist } from "@/hooks/use-caregiver-specialists";
import { logger } from "@/lib/services/logger-service";
import { useChatStore } from "@/lib/store/chat";
import { useAuthStore } from "@/lib/store/auth";
import { useTranslations } from "@/i18n/hooks";
import { Badge } from "@/components/ui/badge";

interface SpecialistCardProps {
  specialist: CaregiverSpecialist;
  onChatClick: () => void;
  onVideoCallClick?: (specialistId: string, specialistName: string) => void;
}

export function SpecialistCard({ specialist, onChatClick, onVideoCallClick }: SpecialistCardProps) {
  const { user } = useAuthStore();
  const { setActiveChat } = useChatStore();
  const t = useTranslations('dashboard');
  
  const isExpired = specialist.agreement_expired;
  const daysLeft = specialist.days_left;
  
  const handleChatClick = () => {
    if (!user) return;
    
    logger.debug('Chat with specialist clicked', { 
      specialistId: specialist.id,
      specialistName: specialist.name
    });
    
    setActiveChat({
      caregiverId: user.id,
      caregiverName: user.user_metadata?.name || user.email || 'Caregiver',
      specialistId: specialist.id,
      specialistName: specialist.name,
      roomId: specialist.roomId || `${specialist.id}-${user.id}`,
      email: user.email || '',
      specialistEmail: specialist.email
    });
    
    onChatClick();
  };

  const handleVideoCallClick = () => {
    if (onVideoCallClick) {
      onVideoCallClick(specialist.id, specialist.name);
    }
  };

  // Get first initial for avatar
  const initial = specialist.name.charAt(0).toUpperCase();

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
      {/* Status Badge */}
      <div className="px-3 sm:px-5 pt-3 sm:pt-4">
        <Badge 
          variant={isExpired ? "destructive" : "default"}
          className={`text-2xs sm:text-xs font-semibold ${
            isExpired 
              ? "bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400" 
              : "bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400"
          }`}
        >
          {isExpired ? "EXPIRED" : "ACTIVE"}
        </Badge>
      </div>

      {/* Specialist Info */}
      <div className="px-3 sm:px-5 py-3 sm:py-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-semibold text-base sm:text-lg">
              {initial}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-foreground truncate">
              {specialist.name}
            </h3>
            <p className="text-2xs sm:text-sm text-muted-foreground">
              Sleep Consultant
            </p>
          </div>
        </div>
      </div>

      {/* Status & Actions */}
      <div className="px-3 sm:px-5 pb-3 sm:pb-5">
        {isExpired ? (
          // Expired state
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm sm:text-base">Agreement Expired</p>
                <p className="text-2xs sm:text-sm text-muted-foreground">
                  Talk to your sleep coach about renewing.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 h-9 sm:h-10 text-sm"
              onClick={handleChatClick}
            >
              <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-2" />
              Renew Agreement
            </Button>
          </div>
        ) : (
          // Active state
          <div className="space-y-3 sm:space-y-4">
            {/* Days Remaining */}
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <span className="font-semibold text-sm sm:text-base text-green-600 dark:text-green-400">
                  {daysLeft} Days Remaining
                </span>
                <p className="text-2xs text-muted-foreground">Active Plan</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 sm:gap-3">
              {onVideoCallClick && (
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium shadow-sm h-9 sm:h-10 text-sm"
                  onClick={handleVideoCallClick}
                >
                  <Video className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                  <span className="hidden xs:inline">Video</span> Call
                </Button>
              )}
              <Button
                variant="outline"
                className="flex-1 border-border hover:bg-accent font-medium h-9 sm:h-10 text-sm"
                onClick={handleChatClick}
              >
                <MessageSquare className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                {t('actions.chat')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
